import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { member } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';
import type { EventRsvp, TeamEvent } from '../types';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Heading } from '../ui/heading';
import { Text, TextLink } from '../ui/text';
import { Panel } from '../components/PageChrome';
import { LocationMap } from '../components/LocationMap';

const fmt = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const recurrenceLabel = (event: TeamEvent) => {
  if (!event.recurrence) return undefined;
  const frequency = event.recurrence.frequency.toLowerCase();
  const until = event.recurrence.until ? ` until ${new Date(event.recurrence.until).toLocaleDateString()}` : '';
  const count = event.recurrenceCount ? ` · ${(event.recurrenceIndex ?? 0) + 1}/${event.recurrenceCount}` : '';
  return `Repeats ${frequency}${until}${count}`;
};

export default function EventDetails() {
  const { teamId, eventId } = useParams<{ teamId: string; eventId: string }>();
  const { user } = useAuth();
  const [event, setEvent] = useState<TeamEvent>();
  const [saved, setSaved] = useState<EventRsvp['status']>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!teamId || !eventId || !user) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await member.events(teamId);
        const found = res.events.find((e) => e.eventId === eventId);
        if (!cancelled) {
          if (found) setEvent(found);
          else setError('Event not found');
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId, eventId, user]);

  const rsvp = async (status: EventRsvp['status']) => {
    if (!teamId || !eventId) return;
    try {
      await member.rsvp(teamId, eventId, { status });
      setSaved(status);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (!user) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <TextLink href={`/teams/${teamId ?? ''}`}>Back to team</TextLink>
        <Panel className="text-center">
          <Heading level={2}>Sign in to view this event</Heading>
          <Button href={`/login?from=${encodeURIComponent(`/teams/${teamId ?? ''}/events/${eventId ?? ''}`)}`} color="dark/zinc" className="mt-4">
            Sign in
          </Button>
        </Panel>
      </div>
    );
  }

  if (!event) {
    return <div className="mx-auto max-w-3xl">{error ? <Text className="text-red-400">{error}</Text> : <Text>Loading event...</Text>}</div>;
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <TextLink href={`/teams/${teamId ?? ''}`}>Back to team</TextLink>
      <Panel className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Badge color={event.type === 'GAME' ? 'red' : event.type === 'PRACTICE' ? 'sky' : 'zinc'}>
            {event.type.replace('_', ' ')}
          </Badge>
          <Badge color={event.status === 'SCHEDULED' ? 'lime' : event.status === 'CANCELLED' ? 'rose' : 'amber'}>
            {event.status}
          </Badge>
          {event.uniform ? <Badge color="zinc">{event.uniform.name}</Badge> : null}
        </div>
        <Heading>{event.title}</Heading>
        <Text>
          {fmt(event.startAt)}
          {event.arrivalAt ? ` - arrive ${fmt(event.arrivalAt)}` : ''}
        </Text>
        {recurrenceLabel(event) ? <Text>{recurrenceLabel(event)}</Text> : null}
        {event.locationName || event.address ? (
          <Text>{[event.locationName, event.address].filter(Boolean).join(' - ')}</Text>
        ) : null}
        {event.warmupAt || event.warmupLocationName || event.warmupAddress ? (
          <Text>
            {event.warmupAt ? `Warmup ${fmt(event.warmupAt)}` : 'Warmup'}
            {event.warmupLocationName || event.warmupAddress
              ? ` - ${[event.warmupLocationName, event.warmupAddress].filter(Boolean).join(' - ')}`
              : ''}
          </Text>
        ) : null}
        {event.notes ? <Text>{event.notes}</Text> : null}
      </Panel>

      {event.locationName || event.address || event.warmupLocationName || event.warmupAddress || event.warmupLatitude !== undefined ? (
        <Panel className="space-y-5">
          <Heading level={2}>Locations</Heading>
          {event.locationName || event.address ? (
            <LocationMap locationName={event.locationName} address={event.address} />
          ) : null}
          {event.warmupLocationName || event.warmupAddress || event.warmupLatitude !== undefined ? (
            <LocationMap
              locationName={event.warmupLocationName}
              address={event.warmupAddress}
              latitude={event.warmupLatitude}
              longitude={event.warmupLongitude}
            />
          ) : null}
        </Panel>
      ) : null}

      {event.rsvpEnabled ? (
        <Panel className="space-y-3">
          <Heading level={2}>RSVP</Heading>
          <div className="flex flex-wrap gap-2">
            {(['GOING', 'MAYBE', 'NOT_GOING'] as const).map((status) =>
              saved === status ? (
                <Button key={status} color="dark/zinc" onClick={() => void rsvp(status)}>
                  {status.replace('_', ' ')}
                </Button>
              ) : (
                <Button key={status} outline onClick={() => void rsvp(status)}>
                  {status.replace('_', ' ')}
                </Button>
              ),
            )}
          </div>
          {saved ? <Text className="text-sm">Saved: {saved.replace('_', ' ')}</Text> : null}
          {error ? <Text className="text-red-400">{error}</Text> : null}
        </Panel>
      ) : null}
    </div>
  );
}
